export type Side = 'north' | 'south';

export interface PaddleState {
  readonly id: string;
  readonly side: Side;
  x: number;
  score: number;
}

export interface BallState {
  x: number;
  z: number;
  vx: number;
  vz: number;
}

export interface WorldState {
  readonly paddles: Map<string, PaddleState>;
  readonly ball: BallState;
  tick: number;
  hitCount: number;
  started: boolean;
  winner: Side | null;
}

export interface MoveAction {
  readonly x: number;
}

export const COURT_WIDTH = 16;
export const COURT_LENGTH = 20;
export const PADDLE_WIDTH = 3.2;
export const PADDLE_Z = 8.7;
export const WIN_SCORE = 7;
const BALL_RADIUS = 0.28;
const PADDLE_DEPTH = 0.45;
const PADDLE_SPEED = 10;
const BALL_SPEED = 8;

export function createWorld(): WorldState {
  const world: WorldState = {
    paddles: new Map(),
    ball: { x: 0, z: 0, vx: 0, vz: 0 },
    tick: 0,
    hitCount: 0,
    started: false,
    winner: null,
  };
  return world;
}

export function addPlayer(world: WorldState, id: string, side?: Side): PaddleState | null {
  const existing = world.paddles.get(id);
  if (existing) return existing;
  if (world.paddles.size >= 2) return null;
  const occupied = new Set([...world.paddles.values()].map((paddle) => paddle.side));
  const assigned = side ?? (occupied.has('south') ? 'north' : 'south');
  if (occupied.has(assigned)) return null;
  const paddle = { id, side: assigned, x: 0, score: 0 };
  world.paddles.set(id, paddle);
  return paddle;
}

export function stepPaddle(paddle: PaddleState, action: MoveAction, deltaSeconds: number): void {
  const limit = COURT_WIDTH / 2 - PADDLE_WIDTH / 2;
  paddle.x = clamp(paddle.x + clamp(action.x, -1, 1) * PADDLE_SPEED * deltaSeconds, -limit, limit);
}

export function stepWorld(world: WorldState, deltaSeconds: number, random = Math.random): void {
  world.tick += 1;
  if (!world.started || world.winner || world.paddles.size < 2) return;

  const ball = world.ball;
  ball.x += ball.vx * deltaSeconds;
  ball.z += ball.vz * deltaSeconds;

  const sideWall = COURT_WIDTH / 2 - BALL_RADIUS;
  if (Math.abs(ball.x) >= sideWall) {
    ball.x = Math.sign(ball.x) * sideWall;
    ball.vx *= -1;
  }

  collidePaddle(world, 'south');
  collidePaddle(world, 'north');

  const goal = COURT_LENGTH / 2 + BALL_RADIUS;
  if (ball.z > goal) scorePoint(world, 'north', random);
  if (ball.z < -goal) scorePoint(world, 'south', random);
}

export function sideForPlayer(world: WorldState, id: string): Side | null {
  return world.paddles.get(id)?.side ?? null;
}

export function restartWorld(world: WorldState): void {
  for (const paddle of world.paddles.values()) {
    paddle.x = 0;
    paddle.score = 0;
  }
  world.tick = 0;
  world.hitCount = 0;
  world.started = false;
  world.winner = null;
  Object.assign(world.ball, { x: 0, z: 0, vx: 0, vz: 0 });
}

export function startWorld(world: WorldState, random = Math.random): void {
  if (world.started || world.winner || world.paddles.size < 2) return;
  world.started = true;
  serve(world, random() < 0.5 ? 'north' : 'south', random);
}

function collidePaddle(world: WorldState, side: Side): void {
  const paddle = [...world.paddles.values()].find((candidate) => candidate.side === side);
  if (!paddle) return;
  const ball = world.ball;
  const z = side === 'south' ? PADDLE_Z : -PADDLE_Z;
  const movingToward = side === 'south' ? ball.vz > 0 : ball.vz < 0;
  if (!movingToward || Math.abs(ball.z - z) > PADDLE_DEPTH + BALL_RADIUS) return;
  const offset = (ball.x - paddle.x) / (PADDLE_WIDTH / 2);
  if (Math.abs(offset) > 1.15) return;

  const direction = side === 'south' ? -1 : 1;
  const speed = ballSpeedForHits(++world.hitCount);
  ball.z = z + direction * (PADDLE_DEPTH + BALL_RADIUS);
  ball.vx = clamp(offset, -1, 1) * speed * 0.85;
  ball.vz = direction * Math.sqrt(Math.max(speed ** 2 - ball.vx ** 2, speed ** 2 * 0.35));
}

function scorePoint(world: WorldState, scoringSide: Side, random: () => number): void {
  const scorer = [...world.paddles.values()].find((paddle) => paddle.side === scoringSide);
  if (scorer) {
    scorer.score += 1;
    if (scorer.score >= WIN_SCORE) world.winner = scoringSide;
  }
  if (!world.winner) serve(world, scoringSide === 'south' ? 'north' : 'south', random);
  else Object.assign(world.ball, { x: 0, z: 0, vx: 0, vz: 0 });
}

export function ballSpeedForHits(hitCount: number): number {
  return BALL_SPEED * 2 ** (hitCount / 10);
}

function serve(world: WorldState, toward: Side, random: () => number): void {
  const angle = (random() - 0.5) * 0.8;
  const speed = ballSpeedForHits(world.hitCount);
  world.ball.x = 0;
  world.ball.z = 0;
  world.ball.vx = Math.sin(angle) * speed;
  world.ball.vz = (toward === 'south' ? 1 : -1) * Math.cos(angle) * speed;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
